import { Money } from '@principle-theorem/accounting';
import { ILogger } from '@principle-theorem/developer-tools';
import {
  IPatient,
  IPricedServiceCodeEntry,
  IStaffer,
  ITreatmentPlan,
  ITreatmentStep,
  PatientStatus,
  TreatmentPlanStatus,
  TreatmentStepStatus,
  type IBrand,
  type IOrganisation,
} from '@principle-theorem/principle-core/interfaces';
import {
  AnyDataTable,
  BatchedQuery,
  DocumentReference,
  Firestore,
  INamedDocument,
  ITimePeriod,
  Timestamp,
  WithRef,
  asyncForEach,
  orderBy,
  sortTimestamp,
  toISODate,
  toMoment,
  undeletedFilter,
  undeletedQuery,
  where,
} from '@principle-theorem/shared';
import { compact, first, groupBy, startCase, sum, toPairs, uniq } from 'lodash';
import * as moment from 'moment-timezone';
import { Appointment } from '../models/appointment/appointment';
import { Brand } from '../models/brand';
import { ClinicalChart } from '../models/clinical-charting/clinical-chart';
import { TreatmentPlan } from '../models/clinical-charting/treatment/treatment-plan';
import { Patient } from '../models/patient/patient';
import { findMobileNumber } from '../models/patient/patient-contact-details';

interface IItemCodeData {
  itemCode: IPricedServiceCodeEntry;
  chartedBy?: INamedDocument<IStaffer>;
  chartedAt?: Timestamp;
  createdAt?: Timestamp;
  source: string;
  status: string;
}

interface IPatientItemCodesData {
  metadata: IPatientMetadata;
  itemCodes: IItemCodeData[];
}

interface IPatientMetadata {
  ref: string;
  name: string;
  patientUrl: string;
  age?: number;
  mobile: string;
  practice?: string;
  healthFund?: string;
  lastVisit?: Timestamp;
  followUpDate?: Timestamp;
  accountCreditRemaining: number;
}

interface IRow {
  name: string;
  patientUrl: string;
  age?: number;
  mobile: string;
  practice?: string;
  healthFund?: string;
  lastVisit?: string;
  followUpDate: string;
  accountCreditRemaining: number;
  practitioner: string;
  source: string;
  status: string;
  chartedAt: string;
  itemCode: string;
  price: number;
}

interface ISummaryRow {
  name: string;
  patientUrl: string;
  age?: number;
  mobile: string;
  practice?: string;
  healthFund?: string;
  lastVisit?: string;
  followUpDate: string;
  accountCreditRemaining: number;
  practitioner: string;
  itemCodes: string;
  outstandingTreatment: number;
}

/**
 * Patients are streamed in pages so that only one page of patient documents
 * is held in memory at a time. Large brands (70k+ patients) previously
 * exhausted the 4GiB function heap when every patient was loaded up front.
 */
const patientPageSize = 500;
const patientConcurrency = 25;

/**
 * Incomplete treatment by item code
 * Gets a list of all item codes from treatments that haven't been completed.
 */
export class IncompleteTreatmentByItemCode {
  constructor(private _logger: ILogger = console) {}

  async run(
    brandRef: DocumentReference<IBrand>,
    dateRange: ITimePeriod,
    appUrl: string
  ): Promise<AnyDataTable[]> {
    const brand = await Firestore.getDoc(brandRef);
    const organisation = await Firestore.getDoc(Brand.organisationRef(brand));
    this._logger.info(`Processing patients for brand ${brand.ref.id}`);

    const data = await BatchedQuery.process(
      Brand.patientCol(brand),
      {
        pageSize: patientPageSize,
        concurrency: patientConcurrency,
        onProgress: (processed) =>
          this._logger.info(`Processed ${processed} patients`),
        onError: (error, patient) =>
          this._logger.error(
            `Error fetching incomplete item codes for patient ${patient.ref.id}`,
            error
          ),
      },
      async (patient) => {
        if (!this._isActivePatient(patient)) {
          return;
        }
        return this._getIncompleteItemCodes(
          brand,
          patient,
          appUrl,
          dateRange,
          organisation
        );
      }
    );

    const rows = this._toRows(data);
    const summary = this._toSummary(data);
    return this._toDataTables(rows, summary);
  }

  private _isActivePatient(patient: WithRef<IPatient>): boolean {
    return undeletedFilter(patient) && patient.status === PatientStatus.Active;
  }

  private async _getIncompleteItemCodes(
    brand: WithRef<IBrand>,
    patient: WithRef<IPatient>,
    appUrl: string,
    dateRange: ITimePeriod,
    organisation: WithRef<IOrganisation>
  ): Promise<IPatientItemCodesData | undefined> {
    const [treatmentPlanItemCodes, flaggedTreatmentItemCodes] =
      await Promise.all([
        this._itemCodesFromTreatmentSteps(patient),
        this._itemCodesFromFlaggedTreatment(patient),
      ]);

    const itemCodes = this._filterToReleventItemCodes(
      [treatmentPlanItemCodes, flaggedTreatmentItemCodes].flat(),
      dateRange
    );
    if (!itemCodes.length) {
      return;
    }

    const [patientMetadata, followUpDate] = await Promise.all([
      this._getPatientMetadata(brand, patient, appUrl, organisation),
      this._getNearestFollowUpDate(patient),
    ]);
    return {
      metadata: { ...patientMetadata, followUpDate },
      itemCodes,
    };
  }

  private _filterToReleventItemCodes(
    itemCodes: IItemCodeData[],
    dateRange: ITimePeriod
  ): IItemCodeData[] {
    return itemCodes
      .filter((data) => data.itemCode.quantity > 0)
      .filter(
        (data) =>
          data.chartedAt &&
          toMoment(data.chartedAt).isBetween(
            dateRange.from,
            dateRange.to,
            'second',
            '[]'
          )
      );
  }

  private async _itemCodesFromTreatmentSteps(
    patient: WithRef<IPatient>
  ): Promise<IItemCodeData[]> {
    const treatmentPlans = await Firestore.getDocs(
      undeletedQuery(TreatmentPlan.col(patient))
    );
    const activeTreatmentPlans = treatmentPlans
      .filter((treatmentPlan) => !treatmentPlan.deleted)
      .filter((treatmentPlan) =>
        [
          TreatmentPlanStatus.Draft,
          TreatmentPlanStatus.Offered,
          TreatmentPlanStatus.Accepted,
          TreatmentPlanStatus.InProgress,
        ].includes(treatmentPlan.status)
      );

    const allSteps = await asyncForEach(
      activeTreatmentPlans,
      async (treatmentPlan) => {
        const steps = await Firestore.getDocs(
          undeletedQuery(TreatmentPlan.treatmentStepCol(treatmentPlan))
        );
        const activeSteps = steps.filter(
          (step) =>
            !step.appointment &&
            !step.deleted &&
            step.status !== TreatmentStepStatus.Complete
        );
        return activeSteps.flatMap((treatmentStep) =>
          this._itemCodesFromTreatmentStep(treatmentPlan, treatmentStep)
        );
      }
    );
    return allSteps.flat();
  }

  private _itemCodesFromTreatmentStep(
    treatmentPlan: WithRef<ITreatmentPlan>,
    treatmentStep: WithRef<ITreatmentStep>
  ): IItemCodeData[] {
    return treatmentStep.treatments.flatMap((treatment) => {
      const surface = first(treatment.chartedSurfaces);
      return treatment.serviceCodes.map((itemCode) => ({
        itemCode,
        chartedAt: surface?.chartedAt ?? treatmentStep.createdAt,
        chartedBy: surface?.chartedBy,
        source: treatmentPlan.name,
        status: startCase(treatmentPlan.status),
        createdAt: treatmentStep.createdAt,
      }));
    });
  }

  private async _itemCodesFromFlaggedTreatment(
    patient: WithRef<IPatient>
  ): Promise<IItemCodeData[]> {
    const chart = await Firestore.firstResult(
      undeletedQuery(ClinicalChart.col(patient)),
      orderBy('createdAt', 'desc')
    );
    if (!chart) {
      return [];
    }

    const treatments = [
      chart.flaggedTreatment.multiTreatments.flatMap((multiTreatment) =>
        multiTreatment.steps.flatMap((step) => step.treatments)
      ),
      chart.flaggedTreatment.treatments,
    ].flat();

    return treatments.flatMap((treatment) => {
      const surface = first(treatment.chartedSurfaces);
      return treatment.serviceCodes.map((itemCode) => ({
        itemCode,
        chartedAt: surface?.chartedAt,
        chartedBy: surface?.chartedBy,
        createdAt: surface?.chartedAt,
        source: 'Flagged Treatment',
        status: 'Flagged',
      }));
    });
  }

  private async _getPatientMetadata(
    brand: WithRef<IBrand>,
    patient: WithRef<IPatient>,
    appUrl: string,
    organisation: WithRef<IOrganisation>
  ): Promise<IPatientMetadata> {
    const lastAppointment = await Patient.lastCompletedAppointment(patient);
    const mobileNumber = this._getMobileNumber(patient, organisation);
    const patientUrl = [appUrl, brand.slug, 'patients', patient.ref.id].join(
      '/'
    );

    const ageInfo = Patient.age(patient);
    const ageInYears = ageInfo?.unit.includes('year')
      ? ageInfo.value
      : undefined;

    return {
      ref: patient.ref.path,
      name: patient.name,
      patientUrl,
      age: ageInYears,
      mobile: mobileNumber,
      practice: patient.preferredPractice?.name,
      healthFund: patient.healthFundCard?.fundCode?.trim(),
      lastVisit: lastAppointment?.event?.from,
      accountCreditRemaining:
        patient.accountSummary.creditSummary.creditRemaining,
    };
  }

  private _getMobileNumber(
    patient: WithRef<IPatient>,
    organisation: WithRef<IOrganisation>
  ): string {
    const mobile = findMobileNumber(
      patient.contactNumbers,
      organisation.region,
      organisation.integrations?.smsProvider
    );
    return mobile?.replace(/ /g, '') ?? '';
  }

  private async _getNearestFollowUpDate(
    patient: WithRef<IPatient>
  ): Promise<Timestamp | undefined> {
    const appointments = await Firestore.getDocs(
      Patient.appointmentCol(patient),
      where('activeFollowUp.createFollowUp', '==', true)
    );
    const now = moment();
    const followUpDates = compact(
      appointments
        .filter(
          (appointment) =>
            !appointment.deleted && Appointment.hasFollowUp(appointment)
        )
        .map((appt) => appt.activeFollowUp?.followUpDate)
    );
    const futureFollowUpDate = followUpDates.filter((date) =>
      toMoment(date).isSameOrAfter(now, 'day')
    );
    if (!futureFollowUpDate.length) {
      return undefined;
    }
    return first(futureFollowUpDate.sort(sortTimestamp));
  }

  private _toRows(data: IPatientItemCodesData[]): IRow[] {
    return data.flatMap((patientData) =>
      patientData.itemCodes.map((itemCode) => ({
        name: patientData.metadata.name,
        patientUrl: patientData.metadata.patientUrl,
        age: patientData.metadata.age,
        mobile: patientData.metadata.mobile,
        practice: patientData.metadata.practice,
        healthFund: patientData.metadata.healthFund,
        lastVisit: patientData.metadata.lastVisit
          ? toISODate(patientData.metadata.lastVisit)
          : '',
        followUpDate: patientData.metadata.followUpDate
          ? toISODate(patientData.metadata.followUpDate)
          : '',
        accountCreditRemaining: patientData.metadata.accountCreditRemaining,
        practitioner: itemCode.chartedBy?.name ?? '',
        source: itemCode.source,
        status: itemCode.status,
        chartedAt: itemCode.chartedAt ? toISODate(itemCode.chartedAt) : '',
        itemCode: `${itemCode.itemCode.code}`,
        price: itemCode.itemCode.priceOverride ?? itemCode.itemCode.price,
      }))
    );
  }

  private _toSummary(data: IPatientItemCodesData[]): ISummaryRow[] {
    return data.map((patientData) => {
      const practitioner = uniq(
        compact(
          patientData.itemCodes.map((itemCode) => itemCode.chartedBy?.name)
        )
      ).join(', ');

      const outstandingTreatment = Money.sum(
        patientData.itemCodes.map((code) =>
          Money.from(code.itemCode.price).multiply(code.itemCode.quantity)
        )
      );

      return {
        name: patientData.metadata.name,
        patientUrl: patientData.metadata.patientUrl,
        age: patientData.metadata.age,
        mobile: patientData.metadata.mobile,
        practice: patientData.metadata.practice,
        healthFund: patientData.metadata.healthFund,
        lastVisit: patientData.metadata.lastVisit
          ? toISODate(patientData.metadata.lastVisit)
          : '',
        followUpDate: patientData.metadata.followUpDate
          ? toISODate(patientData.metadata.followUpDate)
          : '',
        accountCreditRemaining: patientData.metadata.accountCreditRemaining,
        practitioner,
        itemCodes: this._summariseItemCodes(patientData.itemCodes),
        outstandingTreatment: Money.amount(outstandingTreatment),
      };
    });
  }

  private _summariseItemCodes(itemCodes: IItemCodeData[]): string {
    const groupedItemCodes = toPairs(
      groupBy(itemCodes, (itemCode) => itemCode.itemCode.code)
    );
    return groupedItemCodes
      .map(([itemCode, groupedCodes]) => {
        const quantity = sum(
          groupedCodes.map((item) => item.itemCode.quantity)
        );
        return `${itemCode} x${quantity}`;
      })
      .join(', ');
  }

  private _toDataTables(
    rows: IRow[],
    summaryRows: ISummaryRow[]
  ): AnyDataTable[] {
    return [
      {
        name: 'Summary',
        data: summaryRows,
        columns: [
          { key: 'name', header: 'Name' },
          { key: 'patientUrl', header: 'Patient Url' },
          { key: 'age', header: 'Age' },
          { key: 'mobile', header: 'Mobile' },
          { key: 'practice', header: 'Practice' },
          { key: 'practitioner', header: 'Practitioner' },
          { key: 'healthFund', header: 'Health Fund' },
          { key: 'lastVisit', header: 'Last Visit' },
          { key: 'followUpDate', header: 'Follow Up Date' },
          {
            key: 'accountCreditRemaining',
            header: 'Account Credit Remaining',
          },
          { key: 'itemCodes', header: 'Item Codes' },
          { key: 'outstandingTreatment', header: 'Outstanding Treatment' },
        ],
      },
      {
        name: 'Data',
        data: rows,
        columns: [
          { key: 'name', header: 'Name' },
          { key: 'patientUrl', header: 'Patient Url' },
          { key: 'age', header: 'Age' },
          { key: 'mobile', header: 'Mobile' },
          { key: 'practice', header: 'Practice' },
          { key: 'practitioner', header: 'Practitioner' },
          { key: 'source', header: 'Source' },
          { key: 'status', header: 'Status' },
          { key: 'chartedAt', header: 'Created At' },
          { key: 'itemCode', header: 'Item Code' },
          { key: 'price', header: 'Price' },
          { key: 'healthFund', header: 'Health Fund' },
          { key: 'lastVisit', header: 'Last Visit' },
          { key: 'followUpDate', header: 'Follow Up Date' },
          { key: 'accountCreditRemaining', header: 'Account Credit Remaining' },
        ],
      },
    ];
  }
}
